/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { css } from "@emotion/css";
import { Divider, EmptyStateNoResults, Pagination, SearchInput } from "@octopusdeploy/design-system-components";
import type { PrimaryPageAction, PageAction } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { WorkerPoolResource, WorkerPoolsSummaryResource, TenantResource, DynamicWorkerType, Repository } from "@octopusdeploy/octopus-server-client";
import { EndpointsHelper, Permission } from "@octopusdeploy/octopus-server-client";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import { useEffect, useState } from "react";
import * as React from "react";
import { useOnClearMachineDispatch } from "~/areas/infrastructure/hooks/useOnClearMachineDispatch";
import { AdvancedFilterCheckbox } from "~/components/AdvancedFilterLayout";
import { AdvancedFilters } from "~/components/AdvancedFilterLayout/AdvancedFilters";
import { AdvancedFilterTextInput } from "~/components/AdvancedFilterLayout/Text/AdvancedFilterTextInput";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { EndpointCommunicationStyleMultiSelect } from "~/components/MultiSelect/EndpointCommunicationStyleMultiSelect";
import { MachineModelHealthStatusMultiSelect } from "~/components/MultiSelect/MachineModelHealthStatusMultiSelect";
import { ShellNameMultiSelect } from "~/components/MultiSelect/ShellNameMultiSelect";
import { TargetTagMultiSelect } from "~/components/MultiSelect/TargetTagMultiSelect";
import { WorkerPoolMultiSelect } from "~/components/MultiSelect/WorkerPoolMultiSelect";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import ExpansionButtons from "~/components/form/Sections/ExpansionButtons";
import type { TagIndex } from "~/components/tagSets";
import * as tagSets from "~/components/tagSets";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import MachineHealthStatusHelper from "~/utils/MachineHealthStatusHelper";
import PermissionCheck, { isAllowed } from "../../../../../components/PermissionCheck/PermissionCheck";
import ConfirmTentacleUpgradePanel from "../../MachinesLayout/ConfirmTentacleUpgradePanel";
import AddWorkerPoolsDialog from "./AddWorkerPoolsDialog";
import WorkerPoolSummarySection from "./WorkerPoolSummarySection";
import WorkerPoolsSorter from "./WorkerPoolsSorter";
import type { WorkerPoolsSummaryFilter } from "./WorkerPoolsSummaryFilter";
import { defaultWorkerPoolsSummaryFilter, createWorkerPoolsSummaryArgs } from "./WorkerPoolsSummaryFilter";
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
type WorkerPoolsPageQueryParams = WorkerPoolsSummaryFilter;
interface WorkerPoolsPageProps {
    spaceId: string;
    loaderData: WorkerPoolsPageLoaderData;
    queryParams: WorkerPoolsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<WorkerPoolsPageQueryParams>;
}
interface WorkerPoolsPageLoaderData {
    workerPools: WorkerPoolResource[];
    machineRoles: string[];
    tagIndex: TagIndex;
    workerShellNames: string[];
    workerPoolsSummary: WorkerPoolsSummaryResource;
    tenants: TenantResource[];
    workerTypes: DynamicWorkerType[];
}
export const workerPoolsPageTitle = "Worker Pools";
export async function workerPoolsPageLoader(repository: Repository, queryParams: WorkerPoolsPageQueryParams): Promise<WorkerPoolsPageLoaderData> {
    const workerPoolSummaryArgs = createWorkerPoolsSummaryArgs({ ...defaultWorkerPoolsSummaryFilter, ...queryParams });
    const workerPools = repository.WorkerPools.all();
    const machineRoles = repository.MachineRoles.all();
    const workerShellNames = repository.WorkerShells.all();
    const workerPoolsSummary = repository.WorkerPools.summary(workerPoolSummaryArgs);
    const tagIndex = tagSets.getTagIndex(["Tenant"]);
    const workerTypes = repository.WorkerPools.getDynamicWorkerTypes();
    const tenants = await (isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all() : Promise.resolve([]));
    return {
        workerPools: await workerPools,
        machineRoles: await machineRoles,
        tagIndex: await tagIndex,
        workerShellNames: await workerShellNames,
        workerPoolsSummary: await workerPoolsSummary,
        tenants,
        workerTypes: await workerTypes,
    };
}
export function WorkerPoolsPage({ spaceId, loaderData, queryParams, setQueryParams }: WorkerPoolsPageProps) {
    const machineHealthStatuses = MachineHealthStatusHelper.getMachineModelHealthStatusResources();
    const communicationStyles = EndpointsHelper.getCommunicationStyleResources();
    const onClearMachine = useOnClearMachineDispatch();
    const addWorkerPoolDialogControls = useDialogTrigger();
    const navigate = useSpaceAwareNavigation();
    const { doBusyTask, status } = useLegacyDoBusyTask();
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [debouncedPartialName, setDebouncedPartialName] = useDebouncedExternalState(queryParams.partialName, (value) => setQueryParams((prev) => ({ ...prev, partialName: value })), 200);
    const { result: workerPoolsSummary, refetch: reloadWorkerPoolSummary } = useQuery(async (repository) => {
        const workerPoolSummariesArgs = createWorkerPoolsSummaryArgs(queryParams);
        return repository.WorkerPools.summary(workerPoolSummariesArgs);
    }, [queryParams], "Get worker pools summary", { initialResult: loaderData.workerPoolsSummary });
    useEffect(() => {
        onClearMachine();
    }, [onClearMachine]);
    useEffect(() => {
        setCurrentPageIndex(0);
    }, [workerPoolsSummary]);
    const hasWorkerPool = loaderData.workerPools && loaderData.workerPools.length > 0;
    const addWorkerPageAction: PageAction = {
        type: "navigate",
        label: "Add Worker",
        path: links.selectWorkerTypePage.generateUrl({ spaceId }),
        buttonType: "secondary",
        hasPermissions: isAllowed({
            permission: Permission.WorkerEdit,
        }),
    };
    const addWorkerPoolPageAction: PrimaryPageAction = {
        type: "button",
        label: "Add Worker Pool",
        hasPermissions: isAllowed({
            permission: Permission.WorkerEdit,
        }),
        onClick: addWorkerPoolDialogControls.openDialog,
    };
    const pagination = (<Pagination currentPage={currentPageIndex + 1} itemsPerPageOptions={pageSizeOptions} label="Worker Pools" onPageChange={(page) => setCurrentPageIndex(page - 1)} selectedItemsPerPage={pageSize} onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} totalResults={workerPoolsSummary.WorkerPoolSummaries.length}/>);
    const legacyOverflowMenuReorderItem = OverflowMenuItems.dialogItem("Reorder", <WorkerPoolsSorter saveDone={() => reloadWorkerPoolSummary()}/>, { permission: Permission.WorkerView });
    const overflowMenu = OverflowMenuConverterVNext.convertAll([legacyOverflowMenuReorderItem]);
    const start = currentPageIndex * pageSize;
    const end = start + pageSize;
    const workerPoolSummaries = workerPoolsSummary.WorkerPoolSummaries;
    const currentPageWorkerPoolSummaries = workerPoolSummaries.slice(start, end);
    const workerPoolExpanders = currentPageWorkerPoolSummaries.map((summary) => {
        return (<React.Fragment key={summary.WorkerPool.Id}>
                <Divider />
                <WorkerPoolSummarySection workerPoolSummary={summary} workerTypes={loaderData.workerTypes} filter={queryParams} isFiltering={isFiltering(queryParams)} tenants={loaderData.tenants} tagIndex={loaderData.tagIndex}/>
            </React.Fragment>);
    });
    const advancedFilters = (<AdvancedFilters>
            <AdvancedFilterCheckbox label="Disabled only" value={queryParams.isDisabled ?? false} onChange={(x) => setQueryParams((prev) => ({ ...prev, isDisabled: x ? x : undefined }))}/>
            <AdvancedFilterCheckbox label="Hide empty worker pools" value={queryParams.hideEmptyWorkerPools ?? false} onChange={(x) => setQueryParams((prev) => ({ ...prev, hideEmptyWorkerPools: x ? x : undefined }))}/>
            <AdvancedFilterTextInput fieldName={"worker"} value={queryParams.machinePartialName || ""} onChange={(x) => setQueryParams((prev) => ({ ...prev, machinePartialName: x }))}/>
            <WorkerPoolMultiSelect items={loaderData.workerPools ? loaderData.workerPools : []} value={queryParams.workerPoolIds} onChange={(x) => setQueryParams((prev) => ({ ...prev, workerPoolIds: x }))}/>
            <TargetTagMultiSelect items={loaderData.machineRoles} value={queryParams.roles} onChange={(x) => setQueryParams((prev) => ({ ...prev, roles: x }))}/>
            <MachineModelHealthStatusMultiSelect items={machineHealthStatuses} value={queryParams.healthStatuses} onChange={(x) => setQueryParams((prev) => ({ ...prev, healthStatuses: x }))}/>
            <EndpointCommunicationStyleMultiSelect items={communicationStyles} value={queryParams.commStyles} onChange={(x) => setQueryParams((prev) => ({ ...prev, commStyles: x }))}/>
            <ShellNameMultiSelect items={loaderData.workerShellNames ? loaderData.workerShellNames : []} value={queryParams.shellNames} onChange={(x) => setQueryParams((prev) => ({ ...prev, shellNames: x }))}/>
        </AdvancedFilters>);
    const filters = {
        inputs: [<SearchInput placeholder={"Search worker pools..."} accessibleName={"Search by worker pool name"} value={debouncedPartialName ?? ""} onChange={setDebouncedPartialName} autoFocus={true}/>],
        advancedFilters: {
            content: advancedFilters,
            hasUserSelectedValues: isFiltering(queryParams),
            onResetFilter: () => setQueryParams(defaultWorkerPoolsSummaryFilter),
        },
    };
    const tentacleUpgradesRequiredWarning = workerPoolSummaries.some((x) => x.TentacleUpgradesRequired) && (<PermissionCheck permission={Permission.WorkerEdit}>
            <ConfirmTentacleUpgradePanel doBusyTask={doBusyTask} onTentacleUpgradeComplete={() => navigate.navigate(links.tasksPage.generateUrl())}/>
        </PermissionCheck>);
    return (<PageContent header={{
            title: workerPoolsPageTitle,
            primaryAction: addWorkerPoolPageAction,
            pageActions: hasWorkerPool ? [addWorkerPageAction] : undefined,
            overflowActions: hasWorkerPool ? overflowMenu.menuItems : undefined,
        }} filters={filters} pagination={{
            ui: pagination,
            placement: currentPageWorkerPoolSummaries.length > 20 ? "topAndBottom" : "top",
        }} callout={{ type: "custom", content: <>{tentacleUpgradesRequiredWarning}</> }} legacyStatus={status}>
            <BorderedContent>
                {overflowMenu.dialogs}
                <Dialog open={addWorkerPoolDialogControls.isOpen}>
                    <AddWorkerPoolsDialog saveDone={async (workerPool) => {
            addWorkerPoolDialogControls.closeAllDialogs();
            navigate.navigate(links.workerPoolPage.generateUrl({ spaceId, workerPoolId: workerPool.Id }));
        }}/>
                </Dialog>
                {workerPoolExpanders.length > 0 ? (<>
                        <div className={expansionButtonsContainerStyles}>
                            <ExpansionButtons />
                        </div>
                        {workerPoolExpanders}
                    </>) : (<EmptyStateNoResults />)}
            </BorderedContent>
        </PageContent>);
}
function isFiltering(currentFilter: WorkerPoolsSummaryFilter) {
    return !isEqual(currentFilter, defaultWorkerPoolsSummaryFilter);
}
const expansionButtonsContainerStyles = css({
    paddingTop: space[16],
    paddingBottom: space[4],
    paddingLeft: space[16],
});
